// Formatação para exibição. Nada aqui faz conta — as contas moram em
// src/domain. Isto só transforma número em texto legível.

const DIAS_SEMANA = [
  'domingo',
  'segunda-feira',
  'terça-feira',
  'quarta-feira',
  'quinta-feira',
  'sexta-feira',
  'sábado',
] as const;

const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
] as const;

/**
 * Converte 'YYYY-MM-DD' em Date LOCAL.
 *
 * `new Date('2026-09-05')` interpreta como UTC e, em São Paulo (UTC-3), volta
 * dia 4 às 21h. Num sistema cujo registro central é "o dia", isso significaria
 * lançar no dia errado — então toda data do banco entra por aqui.
 */
export function dataLocal(iso: string): Date {
  const [ano, mes, dia] = iso.split('-').map(Number);
  return new Date(ano ?? 1970, (mes ?? 1) - 1, dia ?? 1);
}

/** 'YYYY-MM-DD' a partir de um Date local. Inverso de dataLocal(). */
export function isoLocal(d: Date): string {
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
}

/** Hoje em 'YYYY-MM-DD', no fuso do navegador. */
export function hoje(): string {
  return isoLocal(new Date());
}

/** "05/09/2026" */
export function dataCurta(iso: string): string {
  const d = dataLocal(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

/** "Sábado, 05 de setembro de 2026" — o cabeçalho do mapa, como no papel. */
export function dataPorExtenso(iso: string): string {
  const d = dataLocal(iso);
  const semana = DIAS_SEMANA[d.getDay()] ?? '';
  const mes = MESES[d.getMonth()] ?? '';
  const capitalizado = semana.charAt(0).toUpperCase() + semana.slice(1);
  return `${capitalizado}, ${String(d.getDate()).padStart(2, '0')} de ${mes} de ${d.getFullYear()}`;
}

/** Diferença em dias entre uma data ISO e hoje. Positivo = passado. */
export function diasAtras(iso: string): number {
  const ms = dataLocal(hoje()).getTime() - dataLocal(iso).getTime();
  return Math.round(ms / 86_400_000);
}

/** "há 3 dias", "hoje", "ontem" — para idade de orçamento e sobra em trânsito. */
export function idadeEmDias(iso: string): string {
  const d = diasAtras(iso);
  if (d === 0) return 'hoje';
  if (d === 1) return 'ontem';
  if (d < 0) return `em ${-d} dia${-d > 1 ? 's' : ''}`;
  return `há ${d} dias`;
}

/** Escapa texto antes de entrar em innerHTML. Nome de paciente vem do banco e
 *  não é confiável como HTML.
 *
 *  Aceita só primitivo de propósito: `unknown` deixaria um objeto virar
 *  "[object Object]" na tela sem ninguém perceber. */
export function esc(valor: string | number | boolean | null | undefined): string {
  if (valor === null || valor === undefined) return '';
  return String(valor)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
